<template>
  <section v-if="stops.length" class="ride-poster">
    <header class="poster-head">
      <div class="poster-head-text">
        <p class="poster-kicker">{{ t('posterItinerary') }}</p>
        <h1 class="poster-title">{{ title }}</h1>
      </div>
      <div class="poster-badge" aria-hidden="true">
        <span class="poster-badge-number">{{ totalDays }}</span>
        <span class="poster-badge-label">{{ totalDays === 1 ? t('posterDay') : t('posterDays') }}</span>
      </div>
    </header>

    <div class="poster-timeline">
      <svg class="poster-pin" viewBox="0 0 24 32" aria-hidden="true">
        <path d="M12 0C5.4 0 0 5.3 0 11.9 0 20.8 12 32 12 32s12-11.2 12-20.1C24 5.3 18.6 0 12 0zm0 16.6a4.7 4.7 0 1 1 0-9.4 4.7 4.7 0 0 1 0 9.4z"/>
      </svg>
      <ol class="poster-stops">
        <li
          v-for="(stop, index) in stops"
          :key="`${stop.firstDay}-${stop.accommodationId}`"
          :class="['poster-stop', index % 2 ? 'poster-stop-flipped' : '']"
          :style="{ animationDelay: `${Math.min(index, 10) * 70}ms` }"
        >
          <button
            type="button"
            class="poster-stop-media"
            :aria-label="`${stop.name} · ${dayRangeLabel(stop)}`"
            @click="$emit('open-stop', stop)"
          >
            <span v-if="stop.name" class="poster-name"><span>{{ stop.name }}</span></span>
            <span class="poster-photo-frame">
              <img
                v-if="stop.photo && !brokenPhotos[stop.photo]"
                class="poster-photo"
                :style="{ clipPath: scallop }"
                :src="$store.getters.getResizeImageForMobileDevices(stop.photo)"
                :alt="stop.name"
                loading="lazy"
                @error="markBroken(stop.photo)"
              >
              <span
                v-else
                class="poster-photo poster-photo-empty"
                :style="{ clipPath: scallop }"
              >{{ initial(stop.name) }}</span>
            </span>
            <span class="poster-connector" aria-hidden="true"></span>
          </button>
          <span class="poster-days" @click="$emit('open-stop', stop)">{{ dayRangeLabel(stop) }}</span>
        </li>
      </ol>
    </div>
  </section>
</template>

<script>
import { translationMixin } from "@/mixins/translationMixin";
import { buildPosterStops } from "@/utils/posterStops";

// The photos' wavy-edged circle: twelve soft bumps, as a clip-path polygon.
const SCALLOP = (() => {
  const points = [];
  const steps = 120;
  for (let i = 0; i < steps; i++) {
    const angle = (i / steps) * Math.PI * 2;
    const radius = 46 + 4 * Math.cos(angle * 12);
    const x = 50 + radius * Math.cos(angle);
    const y = 50 + radius * Math.sin(angle);
    points.push(`${x.toFixed(2)}% ${y.toFixed(2)}%`);
  }
  return `polygon(${points.join(', ')})`;
})();

export default {
  name: 'PosterTimeline',
  mixins: [translationMixin],
  props: {
    title: { type: String, default: '' },
    attractionsByDay: { type: Array, default: () => [] },
    // The poster's section of ride.presentation_settings: the manager's
    // splits and photos, or null.
    settings: { type: Object, default: null },
  },
  data() {
    return {
      brokenPhotos: {},
      scallop: SCALLOP,
    };
  },
  computed: {
    stops() {
      return buildPosterStops(this.attractionsByDay, { settings: this.settings });
    },
    totalDays() {
      return this.attractionsByDay.length;
    },
  },
  methods: {
    dayRangeLabel(stop) {
      if (stop.firstDay === stop.lastDay)
        return this.t('dayNumber', { number: stop.firstDay });
      return this.t('posterDayRange', { from: stop.firstDay, to: stop.lastDay });
    },
    initial(name) {
      return (name || '').trim().charAt(0).toUpperCase();
    },
    markBroken(photo) {
      this.$set(this.brokenPhotos, photo, true);
    },
  },
};
</script>

<style scoped>
/* Sizes in px: the app's root font is 10px, so rem here would be tiny. */
.ride-poster {
  --poster-ink: #1d1d1f;
  --poster-muted: #6e6e73;
  --poster-paper: #f4f3f0;
  --poster-chip: #e6e5e1;
  --poster-accent: var(--primary-background, #ef6f5c);
  --poster-accent-text: var(--primary-text-color, #ffffff);
  --poster-photo: clamp(92px, 25vw, 172px);
  --poster-pill: clamp(92px, 24vw, 196px);

  max-width: 760px;
  margin: 20px auto 8px;
  padding: clamp(20px, 5vw, 44px) clamp(12px, 4vw, 40px) clamp(28px, 5vw, 48px);
  background: var(--poster-paper);
  border-radius: 28px;
  color: var(--poster-ink);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 18px 48px -24px rgba(0, 0, 0, 0.25);
}

/* ------------------------------------------------------------------ header */

.poster-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: clamp(16px, 4vw, 32px);
}

.poster-kicker {
  margin: 0 0 6px;
  font-size: clamp(12px, 2.6vw, 15px);
  font-weight: 600;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--poster-muted);
}

.poster-title {
  margin: 0;
  font-size: clamp(26px, 6.5vw, 52px);
  font-weight: 300;
  line-height: 1.05;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--poster-ink);
  overflow-wrap: anywhere;
}

.poster-badge {
  flex-shrink: 0;
  width: clamp(76px, 20vw, 120px);
  height: clamp(76px, 20vw, 120px);
  border-radius: 50%;
  background: var(--poster-ink);
  color: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.poster-badge-number {
  font-size: clamp(28px, 7vw, 46px);
  font-weight: 300;
}

.poster-badge-label {
  margin-top: 4px;
  font-size: clamp(11px, 2.4vw, 15px);
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

/* ---------------------------------------------------------------- timeline */

.poster-timeline {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.poster-pin {
  width: 22px;
  height: 30px;
  fill: var(--poster-ink);
  margin-bottom: 6px;
}

.poster-stops {
  position: relative;
  list-style: none;
  margin: 0;
  padding: 0;
  width: 100%;
}

/* The spine: centred, so it needs no mirroring under RTL. */
.poster-stops::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: calc(var(--poster-photo) / 2);
  left: 50%;
  width: 2px;
  transform: translateX(-50%);
  background: #b9b8b4;
}

.poster-stop {
  position: relative;
  display: grid;
  grid-template-columns: 1fr var(--poster-pill) 1fr;
  align-items: center;
  margin-top: calc(var(--poster-photo) * -0.3);
  animation: poster-rise 0.6s cubic-bezier(0.2, 0.7, 0.2, 1) both;
}

.poster-stop:first-child {
  margin-top: 0;
}

.poster-stop-media {
  grid-column: 1;
  grid-row: 1;
  justify-self: end;
  display: flex;
  align-items: center;
  gap: clamp(6px, 2vw, 14px);
  padding: 0;
  margin: 0;
  border: 0;
  background: none;
  cursor: pointer;
  color: inherit;
  font: inherit;
}

.poster-stop-flipped .poster-stop-media {
  grid-column: 3;
  justify-self: start;
}

.poster-days {
  grid-column: 2;
  grid-row: 1;
  position: relative;
  z-index: 1;
  /* Centred with flex, not text-align: the app's Hebrew rule forces
     text-align: right !important on every element under .home[dir="rtl"]. */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 8px;
  background: var(--poster-accent);
  color: var(--poster-accent-text);
  font-size: clamp(15px, 3.6vw, 21px);
  font-weight: 500;
  letter-spacing: 0.02em;
  white-space: nowrap;
  cursor: pointer;
  box-shadow: 0 6px 16px -10px rgba(0, 0, 0, 0.5);
}

/* The dot and line between a photo and the spine. */
.poster-connector {
  position: relative;
  width: clamp(10px, 3vw, 28px);
  height: 2px;
  background: var(--poster-ink);
  order: 3;
}

.poster-connector::after {
  content: "";
  position: absolute;
  top: 50%;
  inset-inline-start: 0;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: var(--poster-ink);
  transform: translate(-50%, -50%);
}

[dir="rtl"] .poster-connector::after {
  transform: translate(50%, -50%);
}

.poster-stop-flipped .poster-connector {
  order: 0;
}

.poster-stop-flipped .poster-connector::after {
  inset-inline-start: auto;
  inset-inline-end: 0;
  transform: translate(50%, -50%);
}

[dir="rtl"] .poster-stop-flipped .poster-connector::after {
  transform: translate(-50%, -50%);
}

.poster-name {
  display: flex;
  align-items: center;
  justify-content: center;
  width: clamp(30px, 8vw, 42px);
  min-height: calc(var(--poster-photo) * 0.95);
  padding: 14px 0;
  border-radius: 999px;
  background: var(--poster-chip);
  order: 1;
}

.poster-name span {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: clamp(13px, 3vw, 17px);
  font-weight: 700;
  letter-spacing: 0.02em;
  white-space: nowrap;
  max-height: calc(var(--poster-photo) * 1.4);
  overflow: hidden;
  text-overflow: ellipsis;
}

.poster-stop-flipped .poster-name {
  order: 4;
}

.poster-photo-frame {
  order: 2;
  display: block;
  width: var(--poster-photo);
  height: var(--poster-photo);
  filter: drop-shadow(0 10px 18px rgba(0, 0, 0, 0.18));
  transition: transform 0.35s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.poster-stop-media:hover .poster-photo-frame,
.poster-stop-media:focus-visible .poster-photo-frame {
  transform: scale(1.05) rotate(-2deg);
}

.poster-stop-media:focus-visible {
  outline: none;
}

.poster-stop-media:focus-visible .poster-name {
  box-shadow: 0 0 0 3px var(--poster-accent);
}

.poster-photo {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.poster-photo-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  /* --primary-color: --primary-background may itself be a gradient. */
  background: linear-gradient(135deg, var(--primary-color, #ef6f5c), #2b2b2e);
  color: #fff;
  font-size: calc(var(--poster-photo) * 0.38);
  font-weight: 300;
}

@keyframes poster-rise {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .poster-stop {
    animation: none;
  }
  .poster-photo-frame {
    transition: none;
  }
}
</style>
